'use client';

import type { SortDrawByType } from '@svenskaspelab/poolbetting/schema/input';
import { NativeSelect, NativeSelectOption } from '@svenskaspelab/ui/react/select';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type ChangeEvent, type ReactNode, useState } from 'react';

const options: { value: SortDrawByType; text: string }[] = [
  { value: 'regCloseTime', text: 'Spelstopp' },
  { value: 'netSale', text: 'Omsättning' },
  { value: 'extraMoney', text: 'Extrapengar' },
];

const defaultSortBy: SortDrawByType = 'regCloseTime';

function isSortDrawByType(value: string | null): value is SortDrawByType {
  if (value === null) {
    return false;
  }
  return options.some((option) => option.value === value);
}

type DrawSortingSelectProps = {
  className?: string;
};

/**
 * Select component that updates the `sortDrawsBy` search parameter used by
 * SortableDrawList (inline desktop view) and the modal flow.
 *
 * @param root0 component props
 * @param root0.className optional className to override default styles
 * @returns Native select that writes the chosen sort key to the URL.
 */
export default function DrawSortingSelect({ className }: DrawSortingSelectProps): ReactNode {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const sortDrawsByFromUrl = searchParams.get('sortDrawsBy');
  const defaultSortValue = isSortDrawByType(sortDrawsByFromUrl) ? sortDrawsByFromUrl : defaultSortBy;
  const [selectedValue, setSelectedValue] = useState<SortDrawByType>(defaultSortValue);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const sortValue = isSortDrawByType(event.target.value) ? event.target.value : defaultSortBy;
    setSelectedValue(sortValue);

    const currentSearchParams = new URLSearchParams(searchParams);
    currentSearchParams.set('sortDrawsBy', sortValue);
    const newUrl = `${pathname}?${currentSearchParams.toString()}`;
    router.replace(newUrl);
  };

  return (
    <NativeSelect
      className={className}
      aria-label="Sortera omgångar"
      aria-invalid={false}
      value={selectedValue}
      onChange={handleChange}
      name="sorting"
    >
      {options.map((option) => (
        <NativeSelectOption key={option.value} value={option.value}>
          {option.text}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}
